import React from 'react';
import { Box, Divider, IconButton, List, Tooltip, Typography } from '@mui/material';
import { Article, Cancel, History } from '@mui/icons-material';

import { SelectableListItem } from './SelectableListItem';
import type { AgencyListMode, AgencyRow } from './types';

type Props = {
  agencies: AgencyRow[];
  mode?: AgencyListMode;
  selectedNames?: string[];
  selectAllMode?: boolean;
  onToggle: (code: string, name: string) => void;
  onViewHistory?: (agency: AgencyRow) => void;
  onViewLegislations?: (agency: AgencyRow) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  chipColor?: 'primary' | 'secondary';
  activeChipLabel?: string;
};

function isAgencyChecked(
  agency: AgencyRow,
  mode: AgencyListMode,
  selectedNames: string[],
  selectAllMode: boolean,
): boolean {
  if (mode === 'monitoring') return agency.isMonitored;
  if (selectAllMode) return true;
  return selectedNames.includes(agency.name);
}

export function AgencySelectableList({
  agencies,
  mode = 'monitoring',
  selectedNames = [],
  selectAllMode = false,
  onToggle,
  onViewHistory,
  onViewLegislations,
  isLoading = false,
  emptyMessage = 'Nenhuma agência disponível',
  chipColor = 'primary',
  activeChipLabel,
}: Props) {
  if (agencies.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" py={4}>
        <Cancel sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const chipLabel = activeChipLabel ?? (mode === 'selection' ? 'Selecionada' : 'Ativa');

  return (
    <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
      {agencies.map((agency, index) => {
        const checked = isAgencyChecked(agency, mode, selectedNames, selectAllMode);
        const subtitleLines = [`${agency.totalLegislations} legislações cadastradas`];
        if (mode === 'monitoring' && agency.isMonitored && agency.monitoredSince) {
          subtitleLines.push(
            `Monitorada desde ${new Date(agency.monitoredSince).toLocaleDateString('pt-BR')}`,
          );
        }

        const secondaryActions =
          mode === 'monitoring' ? (
            <>
              {agency.isMonitored && onViewLegislations && (
                <Tooltip title="Ver legislações monitoradas">
                  <IconButton edge="end" onClick={() => onViewLegislations(agency)} size="small" color="primary">
                    <Article />
                  </IconButton>
                </Tooltip>
              )}
              {onViewHistory && (
                <Tooltip title="Ver histórico">
                  <IconButton edge="end" onClick={() => onViewHistory(agency)} size="small">
                    <History />
                  </IconButton>
                </Tooltip>
              )}
            </>
          ) : null;

        return (
          <React.Fragment key={agency.code}>
            <SelectableListItem
              primaryLabel={agency.name}
              badge={agency.code.toUpperCase()}
              checked={checked}
              onToggle={() => onToggle(agency.code, agency.name)}
              loading={isLoading}
              chipColor={chipColor}
              activeChipLabel={chipLabel}
              subtitleLines={subtitleLines}
              secondaryActions={secondaryActions}
            />
            {index < agencies.length - 1 && <Divider />}
          </React.Fragment>
        );
      })}
    </List>
  );
}
